import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  applyContentPackage,
  exportContentPackage,
  getContentExportOptions,
  getContentPromotionRuns,
  getContentPromotionStatus,
  previewContentPackage,
} from './AdminAPI';
import './Admin_ContentPromotion.css';

const actionOrder = ['conflict', 'new', 'adopted', 'updated', 'unchanged'];

function formatBytes(bytes) {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  const index = Math.min(
    Math.floor(Math.log(bytes) / Math.log(1024)),
    units.length - 1,
  );
  const value = bytes / (1024 ** index);
  return `${value.toFixed(index === 0 ? 0 : 1)} ${units[index]}`;
}

function formatDate(value) {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleString();
}

function environmentLabel(environment) {
  if (environment === 'pts') return 'PTS';
  if (!environment) return 'Unknown';
  return environment[0].toUpperCase() + environment.slice(1);
}

function issueList(error) {
  const issues = error?.details?.issues;
  if (Array.isArray(issues) && issues.length) return issues;
  return [{
    code: 'request_failed',
    path: '',
    message: error?.message || 'The request failed.',
  }];
}

export function saveContentPackageDownload(result) {
  const url = URL.createObjectURL(result.blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = result.filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

function IssuePanel({ title, issues, tone = 'error' }) {
  if (!issues?.length) return null;
  return (
    <section className={`content-promotion__issues content-promotion__issues--${tone}`}>
      <h3>{title}</h3>
      <ul>
        {issues.map((issue, index) => (
          <li key={`${issue.code}-${issue.path}-${index}`}>
            {issue.displayName ? (
              <>
                <strong>{issue.displayName}</strong>
                {issue.fieldPath && <code>{issue.fieldPath}</code>}
                <small>
                  {issue.resourceType} ID: <code>{issue.sourceId}</code>
                </small>
              </>
            ) : (
              issue.path && <code>{issue.path}</code>
            )}
            <span>{issue.message}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}

function RunHistory({ runs, loading }) {
  return (
    <section className="content-promotion__panel" aria-busy={loading}>
      <div className="content-promotion__section-heading">
        <div>
          <h2>Promotion history</h2>
          <p>Recent completed, failed, and interrupted runs on this environment.</p>
        </div>
      </div>
      {loading && <p role="status">Loading promotion history…</p>}
      {!loading && runs.length === 0 && (
        <p className="content-promotion__empty" role="status">
          No content packages have been applied here yet.
        </p>
      )}
      {!loading && runs.length > 0 && (
        <div className="content-promotion__table-wrap">
          <table>
            <thead>
              <tr>
                <th scope="col">Status</th>
                <th scope="col">Digest</th>
                <th scope="col">Records</th>
                <th scope="col">Media</th>
                <th scope="col">Actor</th>
                <th scope="col">Started</th>
              </tr>
            </thead>
            <tbody>
              {runs.map((run) => {
                const visibleStatus = run.status === 'applying' ? 'incomplete' : run.status;
                const recordTotal = (run.createdCount || 0)
                  + (run.updatedCount || 0)
                  + (run.adoptedCount || 0)
                  + (run.unchangedCount || 0);
                return (
                  <tr key={`${run.packageDigest}-${run.startedAtUtc}`}>
                    <td>
                      <span className={`content-promotion__run-status is-${visibleStatus}`}>
                        {visibleStatus}
                      </span>
                      {run.errorMessage && <small>{run.errorMessage}</small>}
                    </td>
                    <td><code>{run.packageDigest?.slice(0, 12)}</code></td>
                    <td>{recordTotal}</td>
                    <td>{(run.mediaCreatedCount || 0) + (run.mediaReusedCount || 0)}</td>
                    <td>{run.actorName || 'unknown'}</td>
                    <td>{formatDate(run.startedAtUtc)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

export default function AdminContentPromotion() {
  const packageInputRef = useRef(null);
  const [status, setStatus] = useState(null);
  const [runs, setRuns] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [receipt, setReceipt] = useState(null);
  const [confirmation, setConfirmation] = useState('');
  const [ptsVerified, setPtsVerified] = useState(false);
  const [exportReceipt, setExportReceipt] = useState(null);
  const [exportOptions, setExportOptions] = useState([]);
  const [exportOptionsLoading, setExportOptionsLoading] = useState(false);
  const [exportMode, setExportMode] = useState('complete');
  const [selectedExportKeys, setSelectedExportKeys] = useState(() => new Set());
  const [exportSearch, setExportSearch] = useState('');
  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [busy, setBusy] = useState('');

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      setRuns((await getContentPromotionRuns()) || []);
    } catch (error) {
      setIssues(issueList(error));
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  useEffect(() => {
    let active = true;
    async function load() {
      setLoading(true);
      setIssues([]);
      try {
        const loadedStatus = await getContentPromotionStatus();
        if (active) setStatus(loadedStatus);
        if (loadedStatus?.canExport) {
          if (active) setExportOptionsLoading(true);
          const options = await getContentExportOptions();
          if (active) setExportOptions(options?.records || []);
        }
      } catch (error) {
        if (active) setIssues(issueList(error));
      } finally {
        if (active) {
          setLoading(false);
          setExportOptionsLoading(false);
        }
      }
    }
    load();
    loadHistory();
    return () => {
      active = false;
    };
  }, [loadHistory]);

  const groupedChanges = useMemo(() => {
    if (!preview?.changes) return [];
    const groups = preview.changes.reduce((result, change) => {
      if (!result[change.resourceType]) result[change.resourceType] = [];
      result[change.resourceType].push(change);
      return result;
    }, {});
    return Object.entries(groups).sort(([first], [second]) => first.localeCompare(second));
  }, [preview]);

  const groupedExportOptions = useMemo(() => {
    const query = exportSearch.trim().toLowerCase();
    const filtered = exportOptions.filter((option) => {
      if (!query) return true;
      return [
        option.displayName,
        option.resourceType,
        option.naturalKey,
        option.sourceId,
      ].some((value) => value?.toLowerCase().includes(query));
    });
    const groups = filtered.reduce((result, option) => {
      if (!result[option.resourceType]) result[option.resourceType] = [];
      result[option.resourceType].push(option);
      return result;
    }, {});
    return Object.entries(groups).sort(([first], [second]) => (
      first.localeCompare(second)
    ));
  }, [exportOptions, exportSearch]);

  const exportOptionKey = (option) => `${option.resourceType}/${option.sourceId}`;

  const toggleExportRecord = (option) => {
    const key = exportOptionKey(option);
    setSelectedExportKeys((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
    setExportReceipt(null);
  };

  const selectVisibleExportRecords = () => {
    setSelectedExportKeys((current) => {
      const next = new Set(current);
      groupedExportOptions.forEach(([, options]) => {
        options.forEach((option) => next.add(exportOptionKey(option)));
      });
      return next;
    });
    setExportReceipt(null);
  };

  const clearExportSelection = () => {
    setSelectedExportKeys(new Set());
    setExportReceipt(null);
  };

  const selectFile = (event) => {
    const file = event.target.files?.[0] || null;
    setPreview(null);
    setReceipt(null);
    setConfirmation('');
    setPtsVerified(false);
    setIssues([]);
    if (file && !file.name.toLowerCase().endsWith('.plcontent')) {
      setSelectedFile(null);
      setIssues([{
        code: 'invalid_file',
        path: file.name,
        message: 'Choose a Progress Lands .plcontent package.',
      }]);
      return;
    }
    if (file && status?.maxPackageBytes && file.size > status.maxPackageBytes) {
      setSelectedFile(null);
      setIssues([{
        code: 'package_too_large',
        path: file.name,
        message: `The package exceeds the ${formatBytes(status.maxPackageBytes)} limit.`,
      }]);
      return;
    }
    setSelectedFile(file);
  };

  const exportPackage = async () => {
    setBusy('export');
    setIssues([]);
    setExportReceipt(null);
    try {
      const selection = exportMode === 'complete'
        ? null
        : exportOptions
          .filter((option) => selectedExportKeys.has(exportOptionKey(option)))
          .map((option) => ({
            resourceType: option.resourceType,
            sourceId: option.sourceId,
          }));
      const result = await exportContentPackage(selection);
      saveContentPackageDownload(result);
      setExportReceipt(result);
    } catch (error) {
      setIssues(issueList(error));
    } finally {
      setBusy('');
    }
  };

  const previewPackage = async () => {
    if (!selectedFile) return;
    setBusy('preview');
    setIssues([]);
    setPreview(null);
    setReceipt(null);
    setConfirmation('');
    setPtsVerified(false);
    try {
      const result = await previewContentPackage(selectedFile);
      setPreview(result);
    } catch (error) {
      setIssues(issueList(error));
    } finally {
      setBusy('');
    }
  };

  const applyPackage = async () => {
    if (!selectedFile || !preview) return;
    setBusy('apply');
    setIssues([]);
    try {
      const result = await applyContentPackage({
        file: selectedFile,
        previewToken: preview.previewToken,
        confirmationText: confirmation,
        ptsVerified,
      });
      setReceipt(result);
      setConfirmation('');
      await loadHistory();
    } catch (error) {
      setIssues(issueList(error));
    } finally {
      setBusy('');
    }
  };

  const isProduction = status?.environment === 'production';
  const isDevelopment = status?.environment === 'development';
  const expectedSourceEnvironment = isDevelopment ? 'PTS' : 'Development';
  const confirmationMatches = Boolean(
    preview?.requiredConfirmation
    && confirmation === preview.requiredConfirmation,
  );
  const canApply = Boolean(
    selectedFile
    && preview?.canApply
    && confirmationMatches
    && (!isProduction || ptsVerified)
    && !receipt
    && !busy,
  );

  return (
    <main className="content-promotion" aria-busy={loading || Boolean(busy)}>
      <header className="content-promotion__hero">
        <div>
          <p className="content-promotion__eyebrow">Immutable gameplay catalog</p>
          <h1>Content Promotion</h1>
          <p>
            Move immutable gameplay packages between development and PTS, then
            promote the exact development digest tested on PTS to production.
            Target-only records and player data are never changed.
          </p>
        </div>
        {status && (
          <span className={`content-promotion__environment is-${status.environment}`}>
            {environmentLabel(status.environment)}
          </span>
        )}
      </header>

      <div className="content-promotion__announcer" aria-live="polite">
        {busy === 'export' && 'Validating and building the content package…'}
        {busy === 'preview' && 'Uploading and validating the selected package…'}
        {busy === 'apply' && 'Applying the package. Keep this page open…'}
        {receipt?.status === 'completed' && 'The content package was applied successfully.'}
      </div>

      {loading && <p role="status">Loading content promotion status…</p>}
      <IssuePanel title="Action required" issues={issues} />

      {!loading && status?.canExport && (
        <section className="content-promotion__panel content-promotion__panel--primary">
          <div className="content-promotion__section-heading">
            <div>
              <h2>Export {environmentLabel(status.environment)} catalog</h2>
              <p>
                Export the {status.environment} gameplay catalog. The server validates
                every dependency and referenced media file before creating the package.
                Invalid content blocks the download.
              </p>
            </div>
            <button
              className="button-primary"
              type="button"
              disabled={Boolean(
                busy
                || exportOptionsLoading
                || (exportMode === 'selected' && selectedExportKeys.size === 0)
              )}
              onClick={exportPackage}
            >
              {busy === 'export' ? 'Building package…' : 'Export .plcontent'}
            </button>
          </div>
          {status.environment === 'pts' && (
            <p className="content-promotion__direction-note">
              PTS exports can only be synced back to development. Production still
              requires the original development package that was tested on PTS.
            </p>
          )}
          <fieldset className="content-promotion__scope">
            <legend>Package scope</legend>
            <label>
              <input
                type="radio"
                name="export-scope"
                value="complete"
                checked={exportMode === 'complete'}
                onChange={() => {
                  setExportMode('complete');
                  setExportReceipt(null);
                }}
                disabled={Boolean(busy)}
              />
              <span>
                <strong>Complete catalog</strong>
                <small>Recommended for a normal PTS and production promotion.</small>
              </span>
            </label>
            <label>
              <input
                type="radio"
                name="export-scope"
                value="selected"
                checked={exportMode === 'selected'}
                onChange={() => {
                  setExportMode('selected');
                  setExportReceipt(null);
                }}
                disabled={Boolean(busy || exportOptionsLoading)}
              />
              <span>
                <strong>Selected records</strong>
                <small>
                  Export only chosen roots; required dependencies and media are
                  included automatically.
                </small>
              </span>
            </label>
          </fieldset>
          {exportOptionsLoading && (
            <p role="status">Loading exportable gameplay records…</p>
          )}
          {exportMode === 'selected' && !exportOptionsLoading && (
            <div className="content-promotion__selector">
              <div className="content-promotion__selector-tools">
                <label htmlFor="content-export-search">
                  Find gameplay records
                  <input
                    id="content-export-search"
                    type="search"
                    value={exportSearch}
                    onChange={(event) => setExportSearch(event.target.value)}
                    placeholder="Search names, types, keys, or IDs"
                    disabled={Boolean(busy)}
                  />
                </label>
                <div>
                  <button
                    className="button-secondary"
                    type="button"
                    onClick={selectVisibleExportRecords}
                    disabled={Boolean(busy || groupedExportOptions.length === 0)}
                  >
                    Select visible
                  </button>
                  <button
                    className="button-secondary"
                    type="button"
                    onClick={clearExportSelection}
                    disabled={Boolean(busy || selectedExportKeys.size === 0)}
                  >
                    Clear selection
                  </button>
                </div>
              </div>
              <p className="content-promotion__selection-count" role="status">
                {selectedExportKeys.size} selected
              </p>
              {groupedExportOptions.length === 0 ? (
                <p className="content-promotion__empty">
                  No gameplay records match this search.
                </p>
              ) : (
                <div className="content-promotion__selector-groups">
                  {groupedExportOptions.map(([resourceType, options]) => {
                    const selectedInGroup = options.filter((option) => (
                      selectedExportKeys.has(exportOptionKey(option))
                    )).length;
                    return (
                      <details key={resourceType}>
                        <summary>
                          <span>{resourceType}</span>
                          <span>{selectedInGroup}/{options.length}</span>
                        </summary>
                        <ul>
                          {options.map((option) => {
                            const key = exportOptionKey(option);
                            return (
                              <li key={key}>
                                <label>
                                  <input
                                    type="checkbox"
                                    checked={selectedExportKeys.has(key)}
                                    onChange={() => toggleExportRecord(option)}
                                    disabled={Boolean(busy)}
                                  />
                                  <span>
                                    <strong>{option.displayName || option.naturalKey}</strong>
                                    {option.naturalKey
                                      && option.naturalKey !== option.displayName && (
                                        <small>{option.naturalKey}</small>
                                    )}
                                    <small>{option.resourceType} ID: {option.sourceId}</small>
                                  </span>
                                </label>
                              </li>
                            );
                          })}
                        </ul>
                      </details>
                    );
                  })}
                </div>
              )}
              <p className="content-promotion__dependency-note">
                Dependencies are added transitively. Selecting an item, enemy, or
                map never creates a package with missing referenced content.
              </p>
            </div>
          )}
          {exportReceipt && (
            <div className="content-promotion__success" role="status">
              <strong>Package downloaded</strong>
              <code>{exportReceipt.digest}</code>
              <span>
                {exportReceipt.recordCount} records · {exportReceipt.mediaCount} media files
              </span>
              {exportMode === 'selected' && (
                <span>
                  {exportReceipt.selectedRecordCount} selected
                  {' · '}
                  {exportReceipt.dependencyRecordCount} dependencies added
                </span>
              )}
            </div>
          )}
        </section>
      )}

      {!loading && status?.canImport && (
        <>
          <section className="content-promotion__panel content-promotion__panel--primary">
            <div className="content-promotion__section-heading">
              <div>
                <h2>1. Select and preview package</h2>
                <p>{status.workflow}</p>
              </div>
            </div>
            <div className="content-promotion__upload-row">
              <div className="content-promotion__file-picker">
                <strong>{expectedSourceEnvironment} package</strong>
                <input
                  ref={packageInputRef}
                  id="content-package"
                  type="file"
                  accept=".plcontent,application/vnd.progresslands.content+zip"
                  onChange={selectFile}
                  disabled={Boolean(busy)}
                  hidden
                />
                <button
                  type="button"
                  className="button-secondary"
                  disabled={Boolean(busy)}
                  onClick={() => packageInputRef.current?.click()}
                >
                  {selectedFile
                    ? 'Choose another .plcontent file'
                    : 'Choose .plcontent file'}
                </button>
                <small>
                  Select the package downloaded from the {expectedSourceEnvironment} admin page.
                </small>
              </div>
              <button
                type="button"
                className="button-primary"
                disabled={!selectedFile || Boolean(busy)}
                onClick={previewPackage}
              >
                {busy === 'preview' ? 'Validating…' : 'Preview changes'}
              </button>
            </div>
            {selectedFile && (
              <p className="content-promotion__file">
                <strong>{selectedFile.name}</strong>
                <span>{formatBytes(selectedFile.size)}</span>
              </p>
            )}
          </section>

          {preview && (
            <section className="content-promotion__panel">
              <div className="content-promotion__section-heading">
                <div>
                  <h2>2. Review target changes</h2>
                  <p>
                    Digest <code>{preview.contentDigest}</code>
                    {' · '}preview expires {formatDate(preview.previewExpiresAtUtc)}
                  </p>
                </div>
                <span className={`content-promotion__readiness ${preview.canApply ? 'is-ready' : 'is-blocked'}`}>
                  {preview.canApply ? 'Ready to apply' : 'Blocked'}
                </span>
              </div>

              <div className="content-promotion__metrics" aria-label="Preview summary">
                {actionOrder.map((action) => (
                  <div key={action}>
                    <span>{action}</span>
                    <strong>{preview[`${action}Count`] || 0}</strong>
                  </div>
                ))}
                <div>
                  <span>media create</span>
                  <strong>{preview.mediaCreateCount || 0}</strong>
                </div>
                <div>
                  <span>media reuse</span>
                  <strong>{preview.mediaReuseCount || 0}</strong>
                </div>
              </div>

              <IssuePanel title="Blocking issues" issues={preview.errors} />
              <IssuePanel title="Warnings" issues={preview.warnings} tone="warning" />

              <div className="content-promotion__change-groups">
                {groupedChanges.map(([resourceType, changes]) => (
                  <details key={resourceType} open={changes.some((change) => change.action === 'conflict')}>
                    <summary>
                      <span>{resourceType}</span>
                      <span>{changes.length} record{changes.length === 1 ? '' : 's'}</span>
                    </summary>
                    <ul>
                      {changes.map((change) => (
                        <li key={`${change.resourceType}-${change.sourceId}`}>
                          <div>
                            <strong>{change.displayName || change.sourceId}</strong>
                            <code>{change.targetId || change.sourceId}</code>
                          </div>
                          <span className={`content-promotion__action is-${change.action}`}>
                            {change.action}
                          </span>
                          <small>
                            {change.message
                              || (change.changedFields?.length
                                ? `Fields: ${change.changedFields.join(', ')}`
                                : 'No field changes')}
                          </small>
                        </li>
                      ))}
                    </ul>
                  </details>
                ))}
              </div>
            </section>
          )}

          {preview && (
            <section className="content-promotion__panel content-promotion__apply">
              <div>
                <h2>3. Apply exact digest</h2>
                <p>
                  Type <code>{preview.requiredConfirmation}</code> exactly.
                  The server revalidates the package, preview token, and target state.
                </p>
              </div>
              <label htmlFor="promotion-confirmation">
                Confirmation
                <input
                  id="promotion-confirmation"
                  type="text"
                  value={confirmation}
                  autoComplete="off"
                  spellCheck="false"
                  onChange={(event) => setConfirmation(event.target.value)}
                  disabled={Boolean(busy) || !preview.canApply || Boolean(receipt)}
                />
              </label>
              {isProduction && (
                <label className="content-promotion__acknowledgement">
                  <input
                    type="checkbox"
                    checked={ptsVerified}
                    onChange={(event) => setPtsVerified(event.target.checked)}
                    disabled={Boolean(busy) || !preview.canApply || Boolean(receipt)}
                  />
                  <span>
                    I confirm digest <code>{preview.digestPrefix}</code> passed PTS
                    and a fresh production backup was verified.
                  </span>
                </label>
              )}
              <button
                type="button"
                className="button-critical"
                disabled={!canApply}
                onClick={applyPackage}
              >
                {busy === 'apply'
                  ? 'Applying package…'
                  : isDevelopment
                    ? 'Sync to Development'
                    : `Apply to ${environmentLabel(status.environment)}`}
              </button>
              {!preview.canApply && (
                <p className="content-promotion__blocked" role="alert">
                  Resolve every blocker in {environmentLabel(preview.sourceEnvironment)}
                  {' '}and export a new package.
                </p>
              )}
              {receipt && (
                <div className="content-promotion__success" role="status">
                  <strong>Promotion completed</strong>
                  <code>{receipt.packageDigest}</code>
                  <span>
                    {receipt.createdCount || 0} new · {receipt.updatedCount || 0} updated
                    {' · '}{receipt.adoptedCount || 0} adopted
                  </span>
                </div>
              )}
            </section>
          )}
        </>
      )}

      {!loading && status && !status.canExport && !status.canImport && (
        <p className="content-promotion__blocked" role="alert">
          Content promotion is disabled because this server has an invalid environment configuration.
        </p>
      )}

      <RunHistory runs={runs} loading={historyLoading} />
    </main>
  );
}
